/**
 * Muebles y objetos de La Oficina, pintados por código.
 *
 * Cada entrada puede tener:
 *   s(g, o)           parte fija; se guarda en la caché del piso.
 *   d(g, o, env, on)  parte animada; se pinta en cada cuadro.
 *   light(o, env, on) luz que emite: { dx, dy, r, color, a }.
 * `g` es el pincel de pixelKit: x desde el centro y altura hacia arriba,
 * en pixeles lógicos. `on` indica que alguien lo está usando.
 */
import { INK, PX, mix, rgba, shade } from './pixelKit';

const WOOD = '#a06a3a';
const WOOD_D = '#6b4228';
const WOOD_L = '#c48a4a';
const METAL = '#9aa0a8';
const METAL_D = '#5a6270';
const DARK = '#2a3038';
const WHITE = '#f4efe4';
const SCREEN = '#16202c';
const BOOKS = ['#c45c48', '#3d6b9a', '#d4893a', '#4a7c59', '#7a4ea0', '#e0b23c'];
const WARM = '#ffe2a0';

function blinkOn(now, ms, offset) {
    return Math.floor((now + (offset || 0)) / ms) % 2 === 0;
}

function text(g, value, x, top, color, size) {
    g.ctx.font = `bold ${size || 10}px monospace`;
    g.ctx.textAlign = 'center';
    g.ctx.fillStyle = color;
    g.ctx.fillText(value, g.x + x * PX, g.y - top * PX);
    g.ctx.textAlign = 'left';
}

function leaves(g, tone, shape) {
    const dark = shade(tone, -30);
    const lite = shade(tone, 26);
    shape.forEach(([x, top, w, h], index) => {
        g.r(x, top, w, h, index % 3 === 0 ? dark : index % 3 === 1 ? tone : lite);
    });
}

function pot(g, color) {
    g.r(-4, 7, 8, 7, color);
    g.r(-5, 8, 10, 2, shade(color, 24));
    g.r(-3, 2, 6, 2, shade(color, -30));
}

/** Barras que suben y bajan dentro de una pantalla. */
function chartBars(g, x, base, count, now, seed, tone) {
    for (let i = 0; i < count; i += 1) {
        const tall = 2 + Math.round((Math.sin(now / 520 + i * 1.7 + seed) + 1) * 3.5);
        g.r(x + i * 3, base + tall, 2, tall, i % 2 ? tone : shade(tone, 50));
    }
}

export const PROPS = {
    /* ---------- Oficina de proyecto ---------- */
    chair: {
        s(g) {
            g.r(-8, 25, 2, 16, '#3a3a48');
            g.r(-7, 24, 1, 14, '#5a5a70');
            g.r(-7, 8, 12, 2, '#3a3a48');
            g.r(-7, 9, 12, 1, '#5a5a70');
            g.r(-2, 6, 2, 4, METAL_D);
            g.r(-7, 2, 12, 1, METAL_D);
            g.r(-8, 1, 2, 1, INK);
            g.r(-2, 1, 2, 1, INK);
            g.r(4, 1, 2, 1, INK);
        },
    },
    desk: {
        layer: 'front',
        s(g) {
            g.r(-24, 15, 48, 2, WOOD_L);
            g.r(-24, 13, 48, 1, WOOD_D);
            g.r(-23, 12, 2, 12, WOOD_D);
            g.r(9, 12, 14, 12, WOOD);
            g.r(10, 11, 12, 4, WOOD_L);
            g.r(10, 6, 12, 4, WOOD_L);
            g.r(15, 10, 2, 1, WOOD_D);
            g.r(15, 5, 2, 1, WOOD_D);
            g.r(-1, 16, 10, 1, METAL_D);
            g.r(3, 19, 2, 3, METAL_D);
            g.r(-8, 34, 24, 15, DARK);
            g.r(-7, 33, 22, 13, SCREEN);
            g.r(-18, 16, 10, 1, '#d8d0c4');
            g.r(-6, 16, 2, 1, '#d8d0c4');
            g.r(18, 18, 3, 3, WHITE);
            g.r(18, 18, 3, 1, '#6b3e2a');
        },
        d(g, o, env, on) {
            const live = on || o.alert || (env.event && env.event.id === 'screens');
            if (!live) {
                g.r(13, 21, 1, 1, blinkOn(env.now, 900, o.seed) ? '#ffb030' : '#5a4010');
                return;
            }
            const frantic = env.event && env.event.id === 'screens';
            g.r(-7, 33, 22, 13, frantic && blinkOn(env.now, 110) ? '#274a5a' : '#1d3142');
            if (o.alert && !on) {
                const hot = blinkOn(env.now, 320);
                g.r(1, 31, 6, 9, hot ? '#ff3b30' : '#7a1f1a');
                g.r(3, 29, 2, 4, WHITE);
                g.r(3, 24, 2, 1, WHITE);
            } else {
                chartBars(g, -5, 21, 6, env.now * (frantic ? 4 : 1), o.seed, o.tone || '#3ad0c0');
                g.r(-5, 31, 8, 1, '#8fd0ff');
            }
            if (o.alert) {
                g.r(-22, 20, 4, 4, blinkOn(env.now, 260) ? '#ff3b30' : '#7a1f1a');
                g.r(-22, 16, 4, 1, DARK);
            }
        },
        light(o, env, on) {
            if (o.alert && blinkOn(env.now, 260)) return { dx: -60, dy: 56, r: 90, color: '#ff3b30', a: 0.5 };
            if (on) return { dx: 12, dy: 80, r: 84, color: '#8fd8ff', a: 0.42 };
            return null;
        },
    },
    whiteboard: {
        s(g, o) {
            g.r(-16, 63, 32, 22, '#b8b8c2');
            g.r(-15, 62, 30, 20, '#f7f7f2');
            const kind = o.seed % 3;
            if (kind === 0) {
                [3, 6, 4, 9, 7, 11].forEach((tall, i) => g.r(-12 + i * 4, 44 + tall, 3, tall, BOOKS[i % BOOKS.length]));
            } else if (kind === 1) {
                [[-12, 47], [-8, 50], [-4, 49], [0, 54], [4, 53], [8, 58]].forEach(([x, top]) => g.r(x, top, 4, 1, '#c45c48'));
                g.r(-12, 60, 9, 1, '#3d6b9a');
                g.r(-12, 58, 6, 1, '#3d6b9a');
            } else {
                [[-12, 60, '#ffe14a'], [-5, 59, '#ff9ecb'], [3, 60, '#8fe0b0'], [-9, 52, '#8fd0ff'], [1, 51, '#ffe14a']].forEach(([x, top, c]) => g.r(x, top, 5, 5, c));
            }
            g.r(-11, 41, 22, 1, METAL);
            g.r(-8, 42, 3, 1, '#c45c48');
            g.r(-3, 42, 3, 1, '#3d6b9a');
        },
    },
    window: {
        d(g, o, env) {
            g.r(-12, 67, 24, 29, WHITE);
            const sky = env.flash ? '#ffffff' : env.sky.low;
            g.r(-10, 65, 20, 25, sky);
            g.r(-10, 65, 20, 9, env.flash ? '#ffffff' : env.sky.top);
            if (env.dark > 0.55 && !env.flash) {
                [[-7, 62], [3, 59], [6, 63], [-2, 56], [-8, 52]].forEach(([x, top], i) => {
                    if (blinkOn(env.now, 700 + i * 130, o.seed * 37)) g.r(x, top, 1, 1, '#fff6c8');
                });
            } else if (!env.flash) {
                const drift = ((env.now / 900 + o.seed * 7) % 34) - 12;
                const cloud = env.rain ? '#8a93a6' : '#ffffff';
                if (drift > -10 && drift < 6) g.r(Math.round(drift), 60, 5, 2, cloud);
                if (drift > -9 && drift < 5) g.r(Math.round(drift) + 1, 61, 3, 1, cloud);
            }
            if (env.rain) {
                for (let i = 0; i < 5; i += 1) {
                    const fall = Math.floor(env.now / 60 + i * 5 + o.seed) % 22;
                    g.r(-8 + i * 4, 64 - fall, 1, 2, '#d6ecff');
                }
            }
            g.r(-1, 65, 2, 25, WHITE);
            g.r(-10, 53, 20, 1, WHITE);
            g.r(-10, 65, 20, 3, '#e3dccb');
            g.r(-13, 39, 26, 2, shade(WHITE, -26));
        },
        light(o, env) {
            if (env.flash) return { dx: 0, dy: 150, r: 190, color: '#ffffff', a: 0.9 };
            if (env.dark > 0.75) return null;
            return { dx: 0, dy: 150, r: 150, color: '#fff3d0', a: (1 - env.dark) * (env.rain ? 0.2 : 0.42), shaft: true };
        },
    },
    clock: {
        s(g) {
            g.r(-5, 72, 10, 10, DARK);
            g.r(-4, 71, 8, 8, WHITE);
        },
        d(g, o, env) {
            const date = new Date(env.wall);
            const minute = (date.getMinutes() / 60) * Math.PI * 2;
            const hour = (((date.getHours() % 12) + date.getMinutes() / 60) / 12) * Math.PI * 2;
            const cx = g.x;
            const cy = g.y - 67 * PX;
            g.ctx.strokeStyle = INK;
            g.ctx.lineWidth = 2;
            g.ctx.beginPath();
            g.ctx.moveTo(cx, cy);
            g.ctx.lineTo(cx + Math.sin(minute) * 10, cy - Math.cos(minute) * 10);
            g.ctx.moveTo(cx, cy);
            g.ctx.lineTo(cx + Math.sin(hour) * 6, cy - Math.cos(hour) * 6);
            g.ctx.stroke();
        },
    },
    ceilLamp: {
        s(g, o) {
            g.r(0, 80, 1, 7, INK);
            g.r(-5, 74, 10, 3, o.color || '#3d6b9a');
            g.r(-6, 71, 12, 1, shade(o.color || '#3d6b9a', -30));
        },
        d(g, o, env, on) {
            g.r(-2, 70, 4, 1, env.level > 0.45 ? '#fff4c0' : '#8a8270');
            if (env.event && env.event.id === 'disco' && on) g.r(-2, 70, 4, 1, ['#ff5ea8', '#5ee0ff', '#ffe14a'][Math.floor(env.now / 160) % 3]);
        },
        light(o, env) {
            if (env.level < 0.3) return null;
            return { dx: 0, dy: 204, r: 170, color: WARM, a: 0.55 * env.level, cone: true };
        },
    },
    floorLamp: {
        s(g) {
            g.r(-3, 1, 6, 1, DARK);
            g.r(0, 36, 1, 35, DARK);
            g.r(-5, 44, 10, 8, '#f0d890');
            g.r(-4, 45, 8, 1, '#fff0b8');
        },
        light(o, env) {
            return env.level > 0.3 ? { dx: 0, dy: 120, r: 110, color: WARM, a: 0.5 } : null;
        },
    },
    plant: {
        s(g, o) {
            pot(g, o.seed % 2 ? '#b8603a' : '#d8d0c4');
            const tone = ['#3d8f4a', '#2f7a40', '#5a9a3a'][o.seed % 3];
            if (o.seed % 3 === 0) leaves(g, tone, [[-1, 20, 2, 12], [-6, 18, 5, 4], [2, 21, 5, 4], [-5, 13, 4, 3], [2, 15, 4, 4], [-2, 24, 4, 5]]);
            else if (o.seed % 3 === 1) leaves(g, tone, [[-1, 26, 2, 18], [-4, 22, 3, 6], [2, 25, 3, 7], [-6, 16, 3, 5], [4, 18, 3, 6], [-1, 30, 2, 5]]);
            else leaves(g, tone, [[-5, 13, 10, 5], [-7, 16, 5, 4], [3, 17, 5, 5], [-3, 19, 6, 4], [-1, 22, 3, 3], [-6, 11, 3, 2]]);
        },
    },
    bigPlant: {
        s(g, o) {
            g.r(-6, 10, 12, 10, '#5a4a6a');
            g.r(-7, 11, 14, 2, '#74628a');
            const tone = o.seed % 2 ? '#2f7a40' : '#3d8f4a';
            leaves(g, tone, [[-1, 40, 2, 29], [-8, 34, 6, 5], [3, 38, 6, 5], [-10, 26, 6, 5], [4, 29, 7, 5], [-6, 44, 5, 5], [2, 46, 5, 5], [-9, 19, 5, 4], [5, 21, 5, 4], [-2, 50, 4, 6]]);
        },
    },
    cabinet: {
        s(g) {
            g.r(-9, 30, 18, 30, METAL);
            g.r(-9, 30, 18, 1, shade(METAL, 30));
            [28, 19, 10].forEach((top) => {
                g.r(-8, top, 16, 8, shade(METAL, 14));
                g.r(-2, top - 3, 4, 1, METAL_D);
            });
            g.r(-6, 33, 5, 3, '#b8603a');
            g.r(-5, 36, 3, 3, '#3d8f4a');
        },
    },
    shelf: {
        s(g, o) {
            g.r(-13, 52, 26, 1, WOOD_D);
            g.r(-12, 51, 1, 3, WOOD_D);
            g.r(11, 51, 1, 3, WOOD_D);
            let x = -12;
            for (let i = 0; i < 7; i += 1) {
                const tall = 5 + ((o.seed + i * 3) % 4);
                g.r(x, 52 + tall, 2, tall, BOOKS[(o.seed + i) % BOOKS.length]);
                x += 3;
            }
            g.r(9, 55, 3, 3, '#d8d0c4');
            g.r(9, 58, 3, 3, '#3d8f4a');
        },
    },
    poster: {
        s(g, o) {
            const tone = BOOKS[o.seed % BOOKS.length];
            g.r(-7, 66, 14, 18, DARK);
            g.r(-6, 65, 12, 16, '#f7f3ea');
            if (o.seed % 2) {
                g.r(-4, 62, 8, 6, tone);
                g.r(-4, 54, 8, 1, DARK);
                g.r(-4, 52, 5, 1, DARK);
            } else {
                g.r(-5, 58, 10, 8, shade(tone, 40));
                g.r(-5, 54, 4, 4, tone);
                g.r(0, 56, 5, 6, shade(tone, -30));
                g.r(2, 63, 2, 2, '#ffe14a');
            }
        },
    },
    bin: {
        s(g) {
            g.r(-4, 9, 8, 9, '#6a7a8a');
            g.r(-5, 10, 10, 1, '#4a5a6a');
            g.r(-2, 11, 3, 1, WHITE);
        },
    },
    rug: {
        s(g, o) {
            const half = o.w || 24;
            g.r(-half, 2, half * 2, 2, o.color || '#b8475f');
            g.r(-half + 2, 2, half * 2 - 4, 1, shade(o.color || '#b8475f', 34));
            g.r(-half, 2, 1, 2, WHITE);
            g.r(half - 1, 2, 1, 2, WHITE);
        },
    },
    pouf: {
        s(g, o) {
            const tone = o.color || '#d4893a';
            g.r(-7, 8, 14, 8, tone);
            g.r(-6, 9, 12, 1, shade(tone, 30));
            g.r(-7, 2, 14, 2, shade(tone, -30));
        },
    },
    boxes: {
        s(g) {
            const card = '#c49a62';
            g.r(-12, 12, 13, 12, card);
            g.r(2, 9, 11, 9, shade(card, -18));
            g.r(-8, 21, 11, 9, shade(card, 12));
            [[-12, 12, 13], [2, 9, 11], [-8, 21, 11]].forEach(([x, top, w]) => {
                g.r(x, top, w, 1, shade(card, -44));
                g.r(x + Math.floor(w / 2) - 1, top, 2, 4, '#e8d8b0');
            });
        },
    },
    ladder: {
        s(g) {
            for (let i = 0; i < 12; i += 1) {
                g.r(-7 + Math.round(i * 0.5), 3 + i * 3, 1, 3, '#d4893a');
                g.r(7 - Math.round(i * 0.5), 3 + i * 3, 1, 3, shade('#d4893a', -30));
            }
            [8, 16, 24, 32].forEach((top) => g.r(-5, top, 8, 1, '#d4893a'));
            g.r(8, 6, 5, 6, '#d8d8e0');
            g.r(8, 6, 5, 2, '#3d6b9a');
        },
    },
    bulb: {
        s(g) {
            g.r(0, 80, 1, 14, INK);
            g.r(-1, 66, 3, 2, METAL_D);
        },
        d(g, o, env) {
            g.r(-2, 64, 5, 4, env.level > 0.4 ? '#fff0a0' : '#8a8270');
        },
        light(o, env) {
            return env.level > 0.3 ? { dx: 0, dy: 186, r: 130, color: WARM, a: 0.5 * env.level } : null;
        },
    },

    /* ---------- Lobby ---------- */
    reception: {
        layer: 'front',
        s(g) {
            g.r(-15, 18, 30, 18, '#5a3f2e');
            g.r(-16, 19, 32, 2, '#e8dcc8');
            for (let i = 0; i < 5; i += 1) g.r(-13 + i * 6, 15, 1, 14, '#3f2a1e');
            g.r(-5, 13, 10, 7, '#f6e27a');
            g.r(-3, 11, 6, 3, '#5a3f2e');
            g.r(7, 22, 3, 3, '#d8b040');
            g.r(-13, 25, 2, 6, DARK);
            g.r(-14, 27, 6, 1, DARK);
        },
        d(g, o, env, on) {
            if (on) g.r(-13, 25, 1, 5, blinkOn(env.now, 400) ? '#8fd0ff' : '#5ea8d8');
        },
    },
    logoSign: {
        s(g) {
            g.r(-25, 68, 50, 14, '#241c33');
            g.r(-24, 67, 48, 12, '#33284a');
        },
        d(g, o, env) {
            const glow = 0.75 + Math.sin(env.now / 700) * 0.25;
            text(g, 'CENTRAL MEDIA', 0, 59.5, mix('#b89a3a', '#ffe680', glow), 15);
        },
        light(o, env) {
            return { dx: 0, dy: 186, r: 120, color: '#ffe680', a: 0.26 + Math.sin(env.now / 700) * 0.08 };
        },
    },
    sofa: {
        s(g, o) {
            const tone = o.color || '#4a6a8a';
            g.r(-18, 21, 36, 13, shade(tone, -26));
            g.r(-17, 20, 17, 10, shade(tone, -8));
            g.r(1, 20, 16, 10, shade(tone, -8));
            g.r(-21, 15, 4, 13, tone);
            g.r(17, 15, 4, 13, tone);
            g.r(-17, 9, 34, 6, tone);
            g.r(-17, 9, 34, 1, shade(tone, 34));
            g.r(-1, 9, 1, 6, shade(tone, -30));
            g.r(-19, 2, 2, 2, WOOD_D);
            g.r(17, 2, 2, 2, WOOD_D);
            g.r(-15, 17, 6, 6, o.cushion || '#e0b23c');
        },
    },
    coffeeTable: {
        s(g) {
            g.r(-9, 7, 18, 2, WOOD_L);
            g.r(-8, 5, 1, 5, WOOD_D);
            g.r(7, 5, 1, 5, WOOD_D);
            g.r(-6, 8, 5, 1, '#c45c48');
            g.r(-5, 9, 5, 1, '#f7f3ea');
            g.r(3, 10, 3, 3, '#3d8f4a');
        },
    },
    noticeBoard: {
        s(g, o) {
            g.r(-13, 64, 26, 20, WOOD_D);
            g.r(-12, 63, 24, 18, '#c9a06a');
            [[-10, 61, 6, 7, '#f7f3ea'], [-2, 62, 5, 5, '#ffe14a'], [5, 61, 5, 8, '#8fd0ff'], [-9, 52, 5, 5, '#ff9ecb'], [-2, 55, 6, 7, '#f7f3ea'], [6, 51, 4, 4, '#8fe0b0']].forEach(([x, top, w, h, c], i) => {
                g.r(x, top, w, h, c);
                g.r(x + 1, top, 1, 1, BOOKS[(o.seed + i) % BOOKS.length]);
            });
        },
    },
    waterCooler: {
        s(g) {
            g.r(-4, 22, 8, 22, WHITE);
            g.r(-4, 22, 8, 1, shade(WHITE, -30));
            g.r(-3, 14, 2, 2, '#3d6b9a');
            g.r(1, 14, 2, 2, '#c45c48');
            g.r(-3, 10, 6, 1, METAL);
            g.r(-3, 34, 6, 12, '#8ec8e0');
            g.r(-2, 33, 1, 9, '#c8ecf8');
            g.r(-2, 36, 4, 2, '#5ea8d8');
        },
        d(g, o, env, on) {
            const lift = Math.floor(env.now / (on ? 110 : 420) + o.seed) % 9;
            if (on || lift < 4) g.r(1, 24 + lift, 1, 1, '#eafcff');
            if (on) g.r(-1, 23 + ((lift + 4) % 9), 1, 1, '#eafcff');
        },
    },
    printer: {
        s(g) {
            g.r(-8, 14, 16, 14, '#b8b4ac');
            g.r(-7, 12, 14, 5, shade('#b8b4ac', -18));
            g.r(-7, 6, 14, 5, shade('#b8b4ac', -18));
            g.r(-9, 23, 18, 9, WHITE);
            g.r(-9, 23, 18, 1, shade(WHITE, -40));
            g.r(-6, 26, 12, 3, '#d8d0c4');
            g.r(-5, 18, 10, 1, DARK);
        },
        d(g, o, env) {
            const jam = env.event && env.event.id === 'printer';
            g.r(6, 21, 1, 1, jam ? (blinkOn(env.now, 120) ? '#ff3b30' : '#7a1f1a') : (blinkOn(env.now, 1100) ? '#3dff8a' : '#2a8a4a'));
            if (!jam) return;
            for (let i = 0; i < 7; i += 1) {
                const life = ((env.now - env.event.started) / 1000 + i * 0.37) % 1.6;
                const fx = (i % 2 ? 1 : -1) * life * (8 + i * 2);
                const fy = 20 + life * 30 - life * life * 16;
                if (life < 1.5) g.r(Math.round(fx), Math.round(fy), 4, 3, i % 3 ? '#ffffff' : '#f3ecd8');
            }
        },
    },
    entrance: {
        d(g, o, env, on) {
            g.r(-5, 54, 10, 54, '#d8ecf0');
            g.r(-5, 54, 10, 2, DARK);
            g.r(-9, 58, 20, 3, '#c0392b');
            g.r(-9, 55, 20, 1, '#8d2a20');
            if (on) {
                g.r(-5, 52, 2, 52, rgba('#8fd0ea', 0.8));
                g.r(3, 52, 2, 52, rgba('#8fd0ea', 0.8));
            } else {
                g.r(-5, 52, 5, 52, rgba('#8fd0ea', 0.75));
                g.r(0, 52, 5, 52, rgba('#a8dcf0', 0.75));
                g.r(-1, 52, 2, 52, METAL_D);
                g.r(-2, 26, 1, 5, METAL);
                g.r(1, 26, 1, 5, METAL);
            }
            g.r(6, 1, 14, 1, '#7a4a3a');
        },
    },

    /* ---------- Sala ---------- */
    tv: {
        s(g) {
            g.r(-18, 66, 36, 21, DARK);
            g.r(-17, 65, 34, 19, SCREEN);
            g.r(-3, 45, 6, 2, METAL_D);
        },
        d(g, o, env, on) {
            if (!on) {
                g.r(15, 47, 1, 1, '#ff3b30');
                return;
            }
            const mode = Math.floor(env.now / 9000) % 3;
            if (mode === 0) {
                g.r(-17, 65, 34, 19, '#12324a');
                chartBars(g, -14, 48, 9, env.now, o.seed, '#ffb84a');
                g.r(-14, 63, 12, 1, '#8fd0ff');
            } else if (mode === 1) {
                g.r(-17, 65, 34, 19, '#101820');
                const ball = (env.now / 60) % 56;
                const bx = ball < 28 ? ball : 56 - ball;
                const by = Math.abs(((env.now / 90) % 30) - 15);
                g.r(-15 + bx, 49 + by, 1, 1, WHITE);
                g.r(-16, 54 + Math.round(Math.sin(env.now / 400) * 4), 1, 5, WHITE);
                g.r(15, 54 + Math.round(Math.cos(env.now / 430) * 4), 1, 5, WHITE);
            } else {
                g.r(-17, 65, 34, 12, '#5ea8d8');
                g.r(-17, 53, 34, 7, '#4a7c59');
                g.r(-10 + (Math.floor(env.now / 300) % 20), 60, 5, 2, WHITE);
                g.r(6, 58, 5, 5, '#ffe14a');
            }
        },
        light(o, env, on) {
            if (!on) return null;
            const tone = ['#ffb84a', '#b8e8ff', '#8fd0ff'][Math.floor(env.now / 9000) % 3];
            return { dx: 0, dy: 150, r: 150, color: tone, a: 0.3 + Math.sin(env.now / 130) * 0.07 };
        },
    },
    fishtank: {
        s(g) {
            g.r(-11, 15, 22, 15, WOOD_D);
            g.r(-10, 13, 9, 11, WOOD);
            g.r(1, 13, 9, 11, WOOD);
            g.r(-11, 31, 22, 16, DARK);
        },
        d(g, o, env) {
            g.r(-10, 30, 20, 14, '#2a8ab8');
            g.r(-10, 30, 20, 4, '#49a8d0');
            g.r(-10, 18, 20, 2, '#d8c088');
            g.r(-7, 23, 1, 5, '#3d8f4a');
            g.r(6, 22 + (blinkOn(env.now, 600) ? 1 : 0), 1, 4, '#5ab85a');
            [['#ff8a3c', 0, 26], ['#ffe14a', 2.1, 23], ['#ff5ea8', 4.2, 28]].forEach(([tone, phase, top], i) => {
                const swim = Math.sin(env.now / (1300 + i * 400) + phase);
                const fx = Math.round(swim * 7);
                const forward = Math.cos(env.now / (1300 + i * 400) + phase) > 0;
                g.r(fx - 1, top, 3, 2, tone);
                g.r(forward ? fx - 2 : fx + 2, top, 1, 2, shade(tone, -40));
            });
            const rise = Math.floor(env.now / 160) % 11;
            g.r(-4, 19 + rise, 1, 1, '#d8f4ff');
            g.r(3, 19 + ((rise + 5) % 11), 1, 1, '#d8f4ff');
        },
        light() {
            return { dx: 0, dy: 72, r: 80, color: '#5ad0ff', a: 0.4 };
        },
    },
    arcade: {
        s(g) {
            g.r(-8, 46, 16, 46, '#3b2d6b');
            g.r(-8, 46, 2, 46, '#2a2050');
            g.r(-8, 46, 16, 6, '#ffcf3c');
            g.r(-7, 39, 14, 12, DARK);
            g.r(-9, 26, 18, 3, '#55458f');
            g.r(-4, 28, 1, 3, INK);
            g.r(-5, 29, 3, 2, '#e0503c');
            g.r(2, 27, 2, 1, '#3dff8a');
            g.r(5, 27, 2, 1, '#ffe14a');
            g.r(-2, 14, 4, 5, DARK);
            g.r(-1, 12, 2, 1, '#ffcf3c');
        },
        d(g, o, env, on) {
            text(g, 'ARCADE', 0, 41.4, '#3b2d6b', 9);
            const speed = on ? 110 : 260;
            for (let i = 0; i < 4; i += 1) {
                const fall = Math.floor(env.now / speed + i * 3) % 9;
                g.r(-6 + i * 3, 37 - fall, 2, 2, ['#ff5ea8', '#5ee0ff', '#ffe14a', '#3dff8a'][i]);
            }
            g.r(-1 + Math.round(Math.sin(env.now / 300) * 4), 29, 3, 1, WHITE);
        },
        light(o, env) {
            return { dx: 0, dy: 98, r: 78, color: '#c85aff', a: 0.36 + Math.sin(env.now / 200) * 0.06 };
        },
    },
    foosball: {
        layer: 'front',
        s(g) {
            g.r(-14, 17, 28, 6, '#7a4a2a');
            g.r(-14, 17, 28, 1, WOOD_L);
            g.r(-13, 18, 26, 1, '#2e7d4f');
            g.r(-13, 11, 2, 11, WOOD_D);
            g.r(11, 11, 2, 11, WOOD_D);
            g.r(-12, 5, 24, 1, WOOD_D);
        },
        d(g, o, env, on) {
            for (let i = 0; i < 4; i += 1) {
                const jig = on ? Math.round(Math.sin(env.now / 70 + i * 2) * 1.4) : 0;
                g.r(-10 + i * 6 + jig, 21, 2, 3, i % 2 ? '#e0503c' : '#3d6b9a');
                g.r(-10 + i * 6 + jig, 22, 2, 1, '#f0c09a');
                g.r(-10 + i * 6, 15, 2, 2, METAL);
            }
            if (on) g.r(Math.round(Math.sin(env.now / 150) * 9), 19, 1, 1, WHITE);
        },
    },
    beanbag: {
        s(g, o) {
            const tone = o.color || '#7a4ea0';
            g.r(-9, 8, 18, 8, tone);
            g.r(-7, 11, 14, 3, tone);
            g.r(-5, 13, 8, 2, shade(tone, 24));
            g.r(-9, 2, 18, 2, shade(tone, -34));
        },
    },
    discoBall: {
        d(g, o, env) {
            const party = env.event && env.event.id === 'disco';
            g.r(0, 80, 1, 5, INK);
            g.r(-3, 75, 6, 6, '#b8c0cc');
            g.r(-2, 76, 4, 1, '#e8eef6');
            g.r(-3, 73, 6, 1, '#8a92a0');
            if (!party) return;
            const spin = Math.floor(env.now / 120) % 4;
            g.r(-3 + spin, 74, 1, 1, '#ffffff');
            g.r(2 - spin, 72, 1, 1, '#ff5ea8');
            g.r(-1 + (spin % 2), 71, 1, 1, '#5ee0ff');
        },
        light(o, env) {
            if (!(env.event && env.event.id === 'disco')) return null;
            const tone = ['#ff5ea8', '#5ee0ff', '#ffe14a', '#8f5aff'][Math.floor(env.now / 220) % 4];
            return { dx: Math.sin(env.now / 260) * 90, dy: 120, r: 170, color: tone, a: 0.6 };
        },
    },

    /* ---------- Cafetería ---------- */
    counter: {
        s(g) {
            g.r(-17, 16, 34, 16, '#e8dcc8');
            g.r(-18, 17, 36, 2, '#5a4a3a');
            [-16, -5, 6].forEach((x) => {
                g.r(x, 13, 10, 11, shade('#e8dcc8', -16));
                g.r(x + 4, 9, 2, 1, METAL_D);
            });
            g.r(-17, 1, 34, 1, '#5a4a3a');
        },
    },
    coffeeMachine: {
        s(g) {
            g.r(-5, 31, 10, 14, '#b8343a');
            g.r(-5, 31, 10, 3, '#8d2a2e');
            g.r(-4, 27, 8, 4, DARK);
            g.r(-1, 23, 2, 2, METAL);
            g.r(-5, 18, 10, 1, DARK);
            g.r(6, 20, 3, 3, WHITE);
            g.r(9, 21, 3, 3, WHITE);
        },
        d(g, o, env, on) {
            g.r(-3, 26, 1, 1, on ? '#3dff8a' : '#ffb030');
            if (on) {
                g.r(0, 21, 1, 3, '#6b3e2a');
                g.r(-2, 20, 4, 2, WHITE);
            }
            for (let i = 0; i < (on ? 4 : 2); i += 1) {
                const rise = (env.now / (on ? 90 : 170) + i * 4 + o.seed) % 13;
                const sway = Math.round(Math.sin(rise / 2 + i) * 1.3);
                g.r(-1 + sway + i, 33 + Math.round(rise), 1, 1, rgba('#ffffff', 0.75 - rise / 20));
            }
        },
    },
    microwave: {
        s(g) {
            g.r(-7, 26, 14, 9, '#d0d0d8');
            g.r(-6, 25, 9, 7, DARK);
            g.r(4, 25, 2, 7, '#a8a8b4');
        },
        d(g, o, env, on) {
            if (on) {
                g.r(-6, 25, 9, 7, blinkOn(env.now, 300) ? '#ffb84a' : '#e09a30');
                g.r(-4 + (Math.floor(env.now / 200) % 4), 21, 3, 2, '#f7f3ea');
            }
            g.r(4, 24, 1, 1, on ? '#3dff8a' : '#5a6270');
        },
        light(o, env, on) {
            return on ? { dx: -4, dy: 64, r: 56, color: '#ffb84a', a: 0.45 } : null;
        },
    },
    fridge: {
        s(g, o) {
            g.r(-8, 46, 16, 46, '#dfe6ea');
            g.r(-8, 46, 16, 1, shade('#dfe6ea', 20));
            g.r(-8, 31, 16, 1, '#a8b4bc');
            g.r(5, 42, 1, 8, METAL_D);
            g.r(5, 27, 1, 14, METAL_D);
            [[-5, 40, '#ffe14a'], [-2, 37, '#ff9ecb'], [-5, 22, '#8fd0ff'], [0, 25, '#8fe0b0']].forEach(([x, top, c], i) => {
                if ((o.seed + i) % 5) g.r(x, top, 3, 3, c);
            });
            g.r(-8, 2, 16, 2, '#a8b4bc');
        },
        d(g, o, env, on) {
            if (!on) return;
            g.r(-7, 30, 12, 26, '#fff6c8');
            [26, 18, 10].forEach((top) => g.r(-7, top, 12, 1, '#c8d4dc'));
            g.r(-5, 29, 3, 3, '#3d8f4a');
            g.r(0, 22, 2, 4, '#c45c48');
            g.r(-4, 14, 4, 3, '#e0b23c');
            g.r(6, 30, 2, 28, '#c8d4dc');
        },
        light(o, env, on) {
            return on ? { dx: 0, dy: 60, r: 80, color: '#fff6c8', a: 0.5 } : null;
        },
    },
    vending: {
        s(g) {
            g.r(-9, 48, 18, 48, '#c0392b');
            g.r(-9, 48, 18, 2, '#e0503c');
            g.r(-9, 48, 2, 48, '#8d2a20');
            g.r(-6, 44, 10, 26, '#1c2a36');
            g.r(5, 40, 3, 12, '#3a3a46');
            g.r(-6, 14, 12, 6, '#1a1a20');
            g.r(-9, 2, 18, 2, '#5a1a14');
        },
        d(g, o, env, on) {
            g.r(-6, 44, 10, 26, on || blinkOn(env.now, 2600) ? '#24384a' : '#1c2a36');
            for (let row = 0; row < 4; row += 1) {
                for (let col = 0; col < 3; col += 1) {
                    g.r(-5 + col * 3, 42 - row * 6, 2, 4, BOOKS[(row * 3 + col + o.seed) % BOOKS.length]);
                }
                g.r(-6, 37 - row * 6, 10, 1, '#5a6a7a');
            }
            g.r(6, 38, 1, 1, blinkOn(env.now, 500) ? '#3dff8a' : '#2a8a4a');
            if (on && blinkOn(env.now, 240)) g.r(-3, 12, 4, 3, '#e0b23c');
        },
        light(o, env) {
            return { dx: -4, dy: 96, r: 86, color: '#bfe6ff', a: 0.3 + Math.sin(env.now / 900) * 0.05 };
        },
    },
    cafeTable: {
        layer: 'front',
        s(g) {
            g.r(-11, 15, 22, 2, WOOD_L);
            g.r(-11, 13, 22, 1, WOOD_D);
            g.r(-1, 12, 2, 11, METAL_D);
            g.r(-5, 1, 10, 1, METAL_D);
            g.r(-2, 18, 3, 3, '#d8d0c4');
            g.r(-1, 21, 1, 2, '#3d8f4a');
        },
        d(g, o, env) {
            if (!(env.event && env.event.id === 'pizza')) return;
            g.r(-9, 17, 9, 2, '#d8b888');
            g.r(-9, 19, 9, 2, '#c8a070');
            g.r(3, 17, 6, 1, '#f0c040');
            g.r(4, 18, 3, 1, '#c0392b');
        },
    },
    stool: {
        s(g) {
            g.r(-4, 8, 8, 2, '#c0392b');
            g.r(-4, 8, 8, 1, '#e0503c');
            g.r(-3, 6, 1, 6, METAL_D);
            g.r(2, 6, 1, 6, METAL_D);
            g.r(-3, 3, 6, 1, METAL_D);
        },
    },
    pendant: {
        s(g, o) {
            g.r(0, 80, 1, 12, INK);
            g.r(-3, 68, 6, 2, o.color || '#e0503c');
            g.r(-5, 66, 10, 2, o.color || '#e0503c');
        },
        d(g, o, env) {
            g.r(-2, 64, 4, 1, env.level > 0.4 ? '#fff4c0' : '#8a8270');
        },
        light(o, env) {
            return env.level > 0.3 ? { dx: 0, dy: 188, r: 150, color: WARM, a: 0.55 * env.level, cone: true } : null;
        },
    },
    menuBoard: {
        s(g) {
            g.r(-15, 68, 30, 20, WOOD_D);
            g.r(-14, 67, 28, 18, '#22302a');
            [[-12, 64, 12], [-12, 60, 16], [-12, 57, 9], [-12, 54, 14], [-12, 51, 11]].forEach(([x, top, w], i) => {
                g.r(x, top, w, 1, i ? '#d8e8d8' : '#ffe14a');
                if (i) g.r(9, top, 3, 1, '#ff9ecb');
            });
        },
    },
    neon: {
        d(g, o, env) {
            const dead = env.event && env.event.id === 'flicker';
            const lit = dead ? blinkOn(env.now, 90) : Math.floor(env.now / 130 + o.seed) % 41 !== 0;
            g.r(-13, 72, 26, 11, '#2a2030');
            text(g, o.label || 'CAFÉ', 0, 63.5, lit ? '#ff7ac0' : '#6a3a56', 17);
            if (lit) g.r(-11, 62, 22, 1, '#5ee0ff');
        },
        light(o, env) {
            return { dx: 0, dy: 196, r: 120, color: '#ff7ac0', a: 0.34 };
        },
    },

    /* ---------- Baños ---------- */
    stall: {
        s(g) {
            g.r(-11, 56, 22, 56, '#a8bcc0');
            g.r(-10, 54, 20, 54, '#c4d6d8');
            g.r(-3, 9, 8, 9, WHITE);
            g.r(-2, 11, 6, 2, shade(WHITE, -26));
            g.r(-6, 20, 4, 14, WHITE);
            g.r(-6, 20, 4, 1, shade(WHITE, -30));
            g.r(6, 26, 3, 4, '#e8e2d6');
        },
    },
    stallDoor: {
        layer: 'front',
        d(g, o, env, on) {
            g.r(-11, 56, 1, 56, '#7a9498');
            g.r(10, 56, 1, 56, '#7a9498');
            if (on) {
                g.r(-10, 52, 20, 46, '#7fa8a8');
                g.r(-9, 51, 18, 1, '#a8c8c8');
                g.r(-8, 44, 16, 30, '#74989a');
                g.r(6, 30, 2, 2, '#e0503c');
            } else {
                g.r(-10, 52, 4, 46, '#7fa8a8');
                g.r(-10, 52, 1, 46, '#a8c8c8');
                g.r(-7, 30, 1, 2, '#3dff8a');
            }
        },
    },
    sink: {
        s(g) {
            g.r(-7, 16, 14, 4, WHITE);
            g.r(-6, 15, 12, 1, '#b8d8e0');
            g.r(-2, 12, 4, 12, shade(WHITE, -20));
            g.r(-1, 20, 2, 4, METAL);
            g.r(-1, 20, 4, 1, METAL);
            g.r(-6, 44, 12, 18, METAL);
            g.r(-5, 43, 10, 16, '#bfe3ee');
            g.r(-4, 42, 2, 6, '#e8f8fc');
            g.r(-2, 40, 1, 3, '#e8f8fc');
            g.r(5, 20, 2, 3, '#ff9ecb');
        },
        d(g, o, env, on) {
            if (on) {
                g.r(2, 19, 1, 3, '#a8e4ff');
                g.r(1 + (blinkOn(env.now, 90) ? 1 : 0), 17, 2, 1, '#e8f8fc');
            } else if (Math.floor(env.now / 300 + o.seed * 3) % 13 === 0) {
                g.r(2, 18, 1, 1, '#a8e4ff');
            }
        },
    },
    dryer: {
        s(g) {
            g.r(-4, 40, 8, 9, '#d0d0d8');
            g.r(-4, 40, 8, 1, WHITE);
            g.r(-2, 32, 4, 2, METAL_D);
            g.r(2, 38, 1, 1, '#3d6b9a');
        },
        d(g, o, env, on) {
            if (!on) return;
            for (let i = 0; i < 3; i += 1) {
                const fall = Math.floor(env.now / 50 + i * 3) % 8;
                g.r(-2 + i * 2, 29 - fall, 1, 2, '#e8f8fc');
            }
        },
    },
    wetSign: {
        s(g) {
            g.r(-3, 12, 6, 12, '#ffd23c');
            g.r(-4, 3, 2, 3, '#e0b020');
            g.r(2, 3, 2, 3, '#e0b020');
            g.r(-1, 10, 2, 5, INK);
            g.r(-1, 4, 2, 1, INK);
        },
    },

    /* ---------- Pasillo ---------- */
    bench: {
        s(g) {
            g.r(-14, 9, 28, 2, WOOD_L);
            g.r(-14, 7, 28, 1, WOOD_D);
            g.r(-12, 6, 2, 6, METAL_D);
            g.r(10, 6, 2, 6, METAL_D);
        },
    },
    extinguisher: {
        s(g) {
            g.r(-3, 36, 6, 2, WHITE);
            g.r(-2, 33, 4, 10, '#c0392b');
            g.r(-1, 35, 2, 2, METAL_D);
            g.r(-2, 29, 4, 2, WHITE);
        },
    },
    exitSign: {
        s(g) {
            g.r(-9, 76, 18, 7, '#1f6a3a');
            g.r(-8, 75, 16, 5, '#2ea85a');
        },
        d(g) {
            text(g, 'SALIDA', 0, 70.8, '#eafff0', 8);
        },
        light() {
            return { dx: 0, dy: 216, r: 54, color: '#3dff8a', a: 0.3 };
        },
    },
    emergency: {
        d(g, o, env) {
            const alarm = env.event && (env.event.id === 'drill' || env.event.id === 'blackout');
            g.r(-3, 79, 6, 3, '#d8d8e0');
            g.r(-2, 76, 4, 2, alarm && blinkOn(env.now, 220, o.seed * 50) ? '#ff3b30' : '#7a1f1a');
        },
        light(o, env) {
            const alarm = env.event && (env.event.id === 'drill' || env.event.id === 'blackout');
            if (!alarm || !blinkOn(env.now, 220, o.seed * 50)) return null;
            return { dx: 0, dy: 216, r: 200, color: '#ff3b30', a: 0.55 };
        },
    },
};

/* ---------- Exterior: azotea y calle ---------- */
export const OUTDOOR = {
    antenna(g, env) {
        g.r(0, 34, 1, 34, METAL_D);
        g.r(-4, 26, 9, 1, METAL_D);
        g.r(-3, 20, 7, 1, METAL_D);
        g.r(-1, 36, 3, 3, blinkOn(env.now, 800) ? '#ff3b30' : '#6a1a14');
    },
    antennaLight(env) {
        return blinkOn(env.now, 800) ? { dy: 104, r: 46, color: '#ff3b30', a: 0.55 } : null;
    },
    tank(g) {
        g.r(-9, 24, 18, 18, '#8a6a4a');
        g.r(-10, 26, 20, 3, '#6a4a32');
        for (let i = 0; i < 6; i += 1) g.r(-9 + i * 3, 23, 1, 17, '#74563a');
        g.r(-8, 6, 2, 6, METAL_D);
        g.r(6, 6, 2, 6, METAL_D);
        g.r(-9, 14, 18, 1, DARK);
    },
    acUnit(g, env) {
        g.r(-8, 12, 16, 12, '#c8ccd2');
        g.r(-8, 12, 16, 1, WHITE);
        g.r(-6, 10, 9, 8, DARK);
        const spin = Math.floor(env.now / 70) % 2;
        if (spin) {
            g.r(-5, 7, 7, 2, METAL);
        } else {
            g.r(-3, 9, 2, 6, METAL);
        }
        g.r(5, 9, 1, 1, '#3dff8a');
    },
    billboard(g, env) {
        g.r(-30, 30, 2, 30, METAL_D);
        g.r(28, 30, 2, 30, METAL_D);
        g.r(-34, 32, 68, 20, '#241c33');
        g.r(-33, 31, 66, 18, '#33284a');
        text(g, 'CENTRAL MEDIA', 0, 18.6, '#ffe680', 19);
        for (let i = 0; i < 17; i += 1) {
            const lit = (i + Math.floor(env.now / 180)) % 3 === 0;
            g.r(-33 + i * 4, 33, 2, 2, lit ? '#fff0a0' : '#7a6a30');
            g.r(-33 + i * 4, 13, 2, 2, lit ? '#7a6a30' : '#fff0a0');
        }
    },
    pigeon(g, env, seed) {
        const hop = Math.floor(env.now / 700 + seed) % 9 === 0 ? 1 : 0;
        const peck = Math.floor(env.now / 400 + seed * 3) % 5 === 0;
        g.r(-2, 4 + hop, 5, 3, '#8a93a6');
        g.r(peck ? 3 : 2, peck ? 4 + hop : 6 + hop, 2, 2, '#6a7386');
        g.r(peck ? 5 : 4, peck ? 3 + hop : 5 + hop, 1, 1, '#e0a030');
        g.r(-1, 1 + hop, 1, 1, '#c0503c');
    },
    tree(g, env, seed) {
        const sway = env.rain ? Math.round(Math.sin(env.now / 260 + seed) * 1.5) : Math.round(Math.sin(env.now / 1700 + seed) * 0.7);
        g.r(-2, 20, 4, 20, '#6b4228');
        g.r(-1, 20, 1, 20, '#8a5a3a');
        const tone = seed % 2 ? '#3d8f4a' : '#4a9a56';
        g.r(-11 + sway, 40, 22, 14, tone);
        g.r(-8 + sway, 46, 16, 8, tone);
        g.r(-13 + sway, 34, 9, 8, shade(tone, -26));
        g.r(4 + sway, 36, 9, 9, shade(tone, -14));
        g.r(-6 + sway, 44, 8, 4, shade(tone, 26));
    },
    streetLamp(g, env) {
        g.r(-1, 46, 2, 46, DARK);
        g.r(-1, 48, 9, 2, DARK);
        g.r(5, 46, 5, 2, env.dark > 0.3 ? '#fff0a0' : '#b8b4a0');
        g.r(-3, 2, 6, 2, DARK);
    },
    streetLampLight(env) {
        return env.dark > 0.3 ? { dx: 22, dy: 100, r: 130, color: WARM, a: 0.6 * env.dark } : null;
    },
    bush(g, env, seed) {
        const tone = seed % 2 ? '#3d8f4a' : '#4a9a56';
        g.r(-9, 8, 18, 8, tone);
        g.r(-6, 11, 12, 4, tone);
        g.r(-8, 7, 5, 3, shade(tone, -26));
        g.r(1, 9, 4, 2, shade(tone, 28));
        if (seed % 3 === 0) g.r(-3, 9, 2, 2, '#ff9ecb');
    },
};

/* ---------- Visitantes de los eventos ---------- */
export const CRITTERS = {
    cat(g, env, facing) {
        const step = Math.floor(env.now / 110) % 2;
        const f = facing < 0 ? -1 : 1;
        const tone = '#e08a3c';
        const rr = (x, top, w, h, c) => g.r(f > 0 ? x : -x - w, top, w, h, c);
        rr(-6, 8, 11, 5, tone);
        rr(4, 12, 5, 5, tone);
        rr(5, 14, 1, 2, tone);
        rr(8, 14, 1, 2, tone);
        rr(7, 10, 1, 1, INK);
        rr(-8, 11 + step, 2, 6, shade(tone, -30));
        rr(-5 + step * 2, 3, 2, 3, shade(tone, -20));
        rr(2 - step * 2, 3, 2, 3, shade(tone, -20));
        rr(-3, 7, 2, 2, '#f7e8d0');
    },
    roomba(g, env) {
        g.r(-7, 3, 14, 3, '#3a3a46');
        g.r(-6, 4, 12, 1, '#5a5a6a');
        g.r(-1, 4, 2, 1, blinkOn(env.now, 300) ? '#3dff8a' : '#2a8a4a');
        g.r(-8 + (Math.floor(env.now / 120) % 2), 1, 2, 1, '#d8d0c4');
    },
    balloon(g, env, seed) {
        const bob = Math.round(Math.sin(env.now / 500 + seed) * 1.5);
        const tone = BOOKS[seed % BOOKS.length];
        g.r(-4, 16 + bob, 8, 11, tone);
        g.r(-3, 17 + bob, 6, 1, tone);
        g.r(-3, 15 + bob, 2, 4, shade(tone, 50));
        g.r(-1, 5 + bob, 2, 2, shade(tone, -30));
        g.r(0, 3 + bob, 1, 14, '#e8e2d6');
    },
};
